import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Box,
  Input,
} from "@mui/material";
import { useTheme } from "../../hooks/useTheme";
import CloseIcon from "@mui/icons-material/Close";
import { IconButton } from "@mui/material";

const ConfirmationDialog = ({
  open,
  onClose,
  title,
  message,
  confirmText = "Delete",
  cancelText = "Cancel",
  onConfirm,
  showKeepOption = false,
  keepText = "Keep",
  onKeep,
  takeReason=false
}) => {
  const [reason , setReason] = useState("")
  const [showWarning, setShowWarning] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const {theme} = useTheme();

  useEffect(() => {
    if (!open) {
      setReason('');
      setShowWarning(false);
      setIsShaking(false);
    }
  }, [open]);

  return (
    <>
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          15%       { transform: translateX(-6px); }
          30%       { transform: translateX(6px); }
          45%       { transform: translateX(-5px); }
          60%       { transform: translateX(5px); }
          75%       { transform: translateX(-3px); }
          90%       { transform: translateX(3px); }
        }
        .shake { animation: shake 0.5s ease; }
      `}</style>
      <Dialog
      open={open}
      onClose={(event, closeReason) => {
        if (closeReason === 'backdropClick' && takeReason && reason.trim()) {
          setShowWarning(true);
          setIsShaking(true);
          setTimeout(() => setIsShaking(false), 500);
          return;
        }
        onClose();
      }}
      PaperProps={{
        sx: {
          background:`${theme === 'dark' && "linear-gradient(to bottom right, rgba(34,52,97,0.25), rgba(11,10,31,0.05))"}`,
          backdropFilter: "blur(50px)",
          WebkitBackdropFilter: "blur(50px)",
          borderRadius: "12px",
          padding: "10px",
          maxWidth: "500px",
          width: "100%",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.2)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
          transition: "none !important",
        },
      }}
      TransitionProps={{
        timeout: 0,
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: "Poppins",
         color: `${theme === 'dark' ? "white":"#525252"}`,
          fontSize: "1.10rem",
          fontWeight: 600,
        }}
      >
        {title}

        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme === "dark" ? "white" : "#525252",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {!takeReason && <DialogContent>
        <Typography
          sx={{
            fontFamily: "Poppins",
           color: `${theme === 'dark' ? "white":"#525252"}`,
            fontSize: "0.95rem",
            lineHeight: 1.5,
          }}
        >
          {message}
        </Typography>
      </DialogContent>}
      {takeReason &&
        <input
        type="text"
        value={reason}
        onChange={(e) => {
          setReason(e.target.value);
          if (showWarning) setShowWarning(false);
        }}
        placeholder="tell us reason..."
          className={`w-[70%] px-4 h-10 ml-4 focus:outline-none border rounded-full dark:glass-effect dark:text-gray-300
            ${showWarning ? 'border-red-500' : ''}
            ${isShaking ? 'shake' : ''}`}
        />
      }
      <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1, gap: 1 }}>
        {showKeepOption && (
          <Button
            onClick={onKeep}
            sx={{
              fontFamily: "Poppins",
              fontWeight: 300,
              color: `${theme === 'dark' ? "white":"#525252"}`,
            }}
          >
            {keepText}
          </Button>
        )}
        
        <Button
          onClick={onClose}
          sx={{
            fontFamily: "Poppins",
            fontWeight: 300,
            color: `${theme === 'dark' ? "white":"#525252"}`,
            textTransform: "none",
          }}
          className="border-btn dark:!text-white !text-textSecondary !p-2"
        >
          {cancelText} 
        </Button>
        
        <Button
          variant="contained"
          className='disabled:cursor-not-allowed cursor-pointer'
          onClick={() => {
            if (takeReason) {
              onConfirm?.(reason);   // pass reason ONLY when needed
            } else {
              onConfirm?.();         // keep old behavior
            }
          }}
          sx={{
            fontFamily: "Poppins",
            fontWeight: 300,
            background: "linear-gradient(to left, #FB54AE, #2F64FF)",
            color: "white",
            borderRadius: "9999px",
            "&.Mui-disabled": {
              background: "linear-gradient(to left, #FB54AE, #2F64FF)",
              opacity: 0.6,
              color: "white",
            },
            textTransform: "none",
          }}
          disabled={takeReason && reason.trim() === ''} // disable if reason is required but empty
        >
          {confirmText}
        </Button>
      </Box>
      </Dialog>
    </>
  );
};

export default ConfirmationDialog;